import { BusinessClock } from '../../core/time/business-clock.service';
import { clockView } from '../../core/time/business-time';
import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule, AbstractControl, Validators } from '@angular/forms';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberService } from '../../core/services/member.service';
import { StudioService } from '../../core/services/studio.service';
import { QuestionaryService } from '../../core/services/questionary.service';
import { KeyNumberService } from '../../core/services/key-number.service';
import { NotificationService } from '../../core/services/notification.service';
import { TransactionService } from '../../core/services/transaction.service';
import { compressImageForUpload } from '../../shared/utils/image-compression';
import { HttpSrcDirective } from '../../shared/directives/http-src.directive';
import { environment } from '../../../environments/environment';
import { FlashService } from '../../core/services/flash.service';
import { FlashMessageComponent } from '../../shared/components/flash-message.component';

type UniqueField = 'loginId' | 'email';
type UniqueCheck = {
  value: string;
  state: 'idle' | 'pending' | 'valid' | 'duplicate' | 'error';
  request: number;
};

const STATUS_OPTIONS = [
  { id: 0, name: '在籍中' },
  { id: 1, name: '停止' },
  { id: 2, name: '停止(除名)' },
  { id: 4, name: '仮登録' },
  { id: 5, name: '来館アンケート回答' },
  { id: 6, name: '見学予約' },
];

const PAYMENT_METHOD_OPTIONS = [
  { id: 0, name: '口座振替' },
  { id: 1, name: 'クレジットカード' },
];

function groupQuestionaryResults(data: any[]): any[] {
  if (!data?.length) return [];
  return data.reduce((acc: any[], cur: any) => {
    const el = acc.find((p: any) => p.questionaryMaster.questionaryGroupId === cur.questionaryMaster.questionaryGroupId);
    if (el) {
      el.questionaryMaster.selectColumn += '、 ' + cur.questionaryMaster.selectColumn;
      if (cur.inputMemoResult != null) el.inputMemoResult = cur.inputMemoResult;
      if (cur.questionaryMaster.inputMemoContent != null) el.questionaryMaster.inputMemoContent = cur.questionaryMaster.inputMemoContent;
    } else {
      acc.push({
        id: cur.id, memberId: cur.memberId, inputMemoResult: cur.inputMemoResult,
        questionaryMaster: {
          questionaryContent: cur.questionaryMaster.questionaryContent,
          questionaryGroupId: cur.questionaryMaster.questionaryGroupId,
          selectColumn: cur.questionaryMaster.selectColumn,
          freeFlg: cur.questionaryMaster.freeFlg,
          inputMemoContent: cur.questionaryMaster.inputMemoContent,
        },
      });
    }
    return acc;
  }, []);
}

@Component({
    selector: 'app-member-edit',
    imports: [RouterLink, FormsModule, HttpSrcDirective, FlashMessageComponent],
    template: `
<div class="animated fadeIn">
  <div class="row sfapp-container">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <span><i class="fa s-icon-edit"></i>&nbsp;編集</span>
        </div>
        <div class="card-body">
          @if (loading) {
            <div class="text-center py-3"><span class="fa fa-spinner fa-spin fa-2x"></span></div>
          }
          @if (!loading && form) {
            <form novalidate name="memberEditForm" class="col-md-12">
              <!-- 旧版 member-edit.html:11-13 のフォーム先頭 <flash-message> 相当 -->
              <div class="row"><app-flash-message class="col-md-12"></app-flash-message></div>
              <strong>基本情報</strong>
              <hr />
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group row">
                    <div class="col-md-6">
                      <table class="table-bordered table-sm">
                        <thead>
                          <tr>
                            <th width="50%">契約店舗</th>
                            <th width="50%">会員ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{{ form.studio?.name }}</td>
                            <td>{{ form.memberCode }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  @if (form.status !== 5) {
                    <div class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">区分コース</label>
                        <select class="form-control" [(ngModel)]="form.studioPlanId" name="studioPlanId"
                          [disabled]="form.status !== 4 || !selectedKeyNumber" required>
                          <option [ngValue]="null">選択してください</option>
                          @for (p of studioPlans; track p.id) {
                            <option [ngValue]="p.id">{{ p.name }}</option>
                          }
                        </select>
                      </div>
                    </div>
                  }
                  <div class="form-group row">
                    <div class="col-md-6">
                      <label class="col-form-label">入会方法</label>
                      <select class="form-control" [(ngModel)]="form.isWebRegister" name="isWebRegister" required>
                        <option [ngValue]="0">Web入会</option>
                        <option [ngValue]="1">店舗入会</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-group row">
                    <div class="col-md-6">
                      <label class="col-form-label">氏名(漢字)</label>
                      <div class="bisected-input-wrap">
                        <div class="bisected-input">
                          <input type="text" class="form-control" [(ngModel)]="form.lastName" name="lastName" required>
                        </div>
                        <span>&nbsp;</span>
                        <div class="bisected-input">
                          <input type="text" class="form-control" [(ngModel)]="form.firstName" name="firstName" required>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="form-group row">
                    <div class="col-md-6">
                      <label class="col-form-label">氏名(ふりがな)</label>
                      <div class="bisected-input-wrap">
                        <div class="bisected-input">
                          <input type="text" class="form-control" [(ngModel)]="form.lastNameKana" name="lastNameKana" required>
                        </div>
                        <span>&nbsp;</span>
                        <div class="bisected-input">
                          <input type="text" class="form-control" [(ngModel)]="form.firstNameKana" name="firstNameKana" required>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="form-group row">
                    <div class="col-md-6">
                      <label class="col-form-label">性別</label>
                      <select class="col-md-6 form-control" [(ngModel)]="form.sex" name="sex" required>
                        <option [ngValue]="0">男性</option>
                        <option [ngValue]="1">女性</option>
                      </select>
                    </div>
                  </div>
                  <div class="row align-items-end">
                    <div class="col-md-6 form-group">
                      <label class="col-form-label">郵便番号</label>
                      <div class="bisected-input-wrap">
                        <div class="bisected-input">
                          <input type="text" class="form-control" [(ngModel)]="form.zip1" name="zip1" required pattern="^[\x01-\x7E]+$">
                        </div>
                        <span>&nbsp;</span>
                        <div class="bisected-input">
                          <input type="text" class="form-control" [(ngModel)]="form.zip2" name="zip2" required pattern="^[\x01-\x7E]+$">
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6 form-group">
                      <button type="button" class="btn btn-primary text-white" (click)="searchZipCode()">
                        <i class="fa s-icon-autoAddress"></i>&nbsp;住所自動入力
                      </button>
                    </div>
                  </div>
                  <div class="form-group row">
                    <div class="col-md-12">
                      <label class="col-form-label">住所</label>
                      <div class="quartered-input-wrap">
                        <div class="quartered-input">
                          <input type="text" class="form-control" [(ngModel)]="form.province" name="province" required>
                        </div>
                        <span>&nbsp;</span>
                        <div class="quartered-input">
                          <input type="text" class="form-control" [(ngModel)]="form.city" name="city" required>
                        </div>
                        <span>&nbsp;</span>
                        <div class="quartered-input">
                          <input type="text" class="form-control" [(ngModel)]="form.area" name="area" required>
                        </div>
                        <span>&nbsp;</span>
                        <div class="quartered-input">
                          <input type="text" class="form-control" [(ngModel)]="form.address" name="address">
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="form-group row">
                    <div class="col-md-6">
                      <label class="col-form-label">生年月日</label>
                      <div class="trisection-input-wrap">
                        <div class="trisection-input">
                          <select class="form-control" [(ngModel)]="form.birth1" name="birth1" required>
                            @for (y of birthYears; track y) {
                              <option [ngValue]="y">{{y}}</option>
                            }
                          </select>
                        </div>
                        <span>&nbsp;</span>
                        <div class="trisection-input">
                          <select class="form-control" [(ngModel)]="form.birth2" name="birth2" required>
                            @for (m of birthMonths; track m) {
                              <option [ngValue]="m">{{m}}</option>
                            }
                          </select>
                        </div>
                        <span>&nbsp;</span>
                        <div class="trisection-input">
                          <select class="form-control" [(ngModel)]="form.birth3" name="birth3" required>
                            @for (d of birthDates; track d) {
                              <option [ngValue]="d">{{d}}</option>
                            }
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="form-group row">
                    <div class="col-md-6">
                      <label class="col-form-label">国籍</label>
                      <select class="col-md-6 form-control" [(ngModel)]="form.isJapanese" name="isJapanese" required>
                        <option [ngValue]="0">日本</option>
                        <option [ngValue]="1">日本以外</option>
                      </select>
                    </div>
                  </div>
                  @if (form.status !== 5) {
                    <div class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">ログインID</label>
                        @if (uniqueChecks.loginId.state === 'duplicate') {
                          <label class="text-danger" role="alert">すでに使用されています。</label>
                        }
                        @if (uniqueChecks.loginId.state === 'error') {
                          <label class="text-danger" role="alert">重複確認に失敗しました。もう一度入力欄からフォーカスを移してください。</label>
                        }
                        <input type="text" class="form-control" [(ngModel)]="form.loginId" name="loginId" required pattern="^[\x01-\x7E]+$"
                          (ngModelChange)="onUniqueValueChange('loginId')" (blur)="checkDuplicate('loginId')">
                      </div>
                    </div>
                  }
                  @if (form.status !== 4 && form.status !== 5) {
                    <div class="form-group row align-items-end">
                      <div class="col-md-6">
                        <label class="col-form-label">パスワード</label>
                        <div>
                          <button type="button" class="btn btn-success" (click)="showResetPasswordModal = true">
                            <i class="fa s-icon-passward"></i>&nbsp;パスワードリセット
                          </button>
                        </div>
                      </div>
                    </div>
                  }
                  <div class="row align-items-end">
                    <div class="col-md-6 form-group">
                      <label class="col-form-label">メールアドレス</label>
                      @if (uniqueChecks.email.state === 'duplicate') {
                        <label class="text-danger" role="alert">すでに使用されています。</label>
                      }
                      @if (uniqueChecks.email.state === 'error') {
                        <label class="text-danger" role="alert">重複確認に失敗しました。もう一度入力欄からフォーカスを移してください。</label>
                      }
                      <input type="email" class="form-control" [(ngModel)]="form.email" name="email" email
                        (ngModelChange)="onUniqueValueChange('email')" (blur)="checkDuplicate('email')">
                    </div>
                    @if ((form.status === 1 || form.status === 2) && form.email) {
                      <div class="col-md-6 form-group">
                        <button type="button" class="btn btn-danger text-white" (click)="showDeleteEmailModal = true">
                          <i class="fa s-icon-delete"></i>&nbsp;メールアドレス削除
                        </button>
                      </div>
                    }
                  </div>
                  <div class="row align-items-end">
                    <div class="col-md-6 form-group">
                      <label class="col-form-label">メール配信</label>
                      <div class="custom-control custom-radio">
                        <div class="custom-control custom-radio custom-control-inline">
                          <input type="radio" id="is_deliver_email_0" class="custom-control-input"
                            [(ngModel)]="form.isDeliverEmail" [value]="0" name="isDeliverEmail">
                          <label class="custom-control-label" for="is_deliver_email_0">許可</label>
                        </div>
                        <div class="custom-control custom-radio custom-control-inline">
                          <input type="radio" id="is_deliver_email_1" class="custom-control-input"
                            [(ngModel)]="form.isDeliverEmail" [value]="1" name="isDeliverEmail">
                          <label class="custom-control-label" for="is_deliver_email_1">禁止</label>
                        </div>
                      </div>
                    </div>
                    @if (form.status === 1) {
                      <div class="col-md-6 form-group">
                        <button type="button" class="btn btn-success text-white" (click)="changeIsDeliverEmail()">
                          <i class="fa s-icon-save"></i>&nbsp;メール配信状態更新
                        </button>
                      </div>
                    }
                  </div>
                  <div class="form-group row">
                    <div class="col-md-6">
                      <label class="col-form-label">電話番号</label>
                      <div class="trisection-input-wrap">
                        <div class="trisection-input">
                          <input type="text" class="form-control" [(ngModel)]="form.phone1" name="phone1" required pattern="^[\x01-\x7E]+$">
                        </div>
                        <span>&nbsp;</span>
                        <div class="trisection-input">
                          <input type="text" class="form-control" [(ngModel)]="form.phone2" name="phone2" required pattern="^[\x01-\x7E]+$">
                        </div>
                        <span>&nbsp;</span>
                        <div class="trisection-input">
                          <input type="text" class="form-control" [(ngModel)]="form.phone3" name="phone3" required pattern="^[\x01-\x7E]+$">
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row align-items-end">
                    <div class="col-md-9 form-group">
                      <label class="col-form-label">備考</label>
                      <input type="text" class="form-control" [(ngModel)]="form.note" name="note">
                    </div>
                    @if (form.status === 1 || form.status === 2) {
                      <div class="col-md-3 form-group">
                        <button type="button" class="btn btn-success text-white" (click)="updateNote()">
                          <i class="fa s-icon-save"></i>&nbsp;備考更新
                        </button>
                      </div>
                    }
                  </div>
                </div>
              </div>
              @if (form.status !== 5) {
                <br /><br />
                <strong>本人画像</strong>
                <hr />
                <div class="col-md-12">
                  <div class="form-group row">
                    <div class="col-md-10">
                      <div class="row">
                        <div class="col-md-6">
                          <p>
                            <button class="btn btn-outline-primary btn-sm" type="button" (click)="showUploadModal1 = true">
                              <i class="fa s-icon-plus"></i>&nbsp;画像をアップロード：1枚目
                            </button>
                          </p>
                          @if (form.uploadedImg1) {
                            <p>
                              <button class="btn btn-outline-danger btn-sm" type="button" (click)="showRemoveModal1 = true">
                                <i class="fa s-icon-delete"></i>&nbsp;削除：1枚目
                              </button>
                            </p>
                            <div class="rorate-area">
                              <!-- 旧版 member-edit.html は1枚目だけ回転処理中(isRotatable=false)に回転アイコンを隠す -->
                              @if (isRotatable) {
                                <div class="rorate-box">
                                  <i class="fa fa-rotate-left rorate-allow" aria-hidden="true" (click)="rotateImage(1, 90)"></i>
                                  <i class="fa fa-rotate-right rorate-allow" aria-hidden="true" (click)="rotateImage(1, -90)"></i>
                                </div>
                              }
                              <img [httpSrc]="imageSrc1" class="img-thumbnail">
                            </div>
                          }
                          @if (!form.uploadedImg1) {
                            <span>アップロード済みの画像はありません</span>
                          }
                        </div>
                        <div class="col-md-6">
                          <p>
                            <button class="btn btn-outline-primary btn-sm" type="button" (click)="showUploadModal2 = true">
                              <i class="fa s-icon-plus"></i>&nbsp;画像をアップロード：2枚目
                            </button>
                          </p>
                          @if (form.uploadedImg2) {
                            <p>
                              <button class="btn btn-outline-danger btn-sm" type="button" (click)="showRemoveModal2 = true">
                                <i class="fa s-icon-delete"></i>&nbsp;削除：2枚目
                              </button>
                            </p>
                            <div class="rorate-area">
                              <div class="rorate-box">
                                <i class="fa fa-rotate-left rorate-allow" aria-hidden="true" (click)="rotateImage(2, 90)"></i>
                                <i class="fa fa-rotate-right rorate-allow" aria-hidden="true" (click)="rotateImage(2, -90)"></i>
                              </div>
                              <img [httpSrc]="imageSrc2" class="img-thumbnail">
                            </div>
                          }
                          @if (!form.uploadedImg2) {
                            <span>アップロード済みの画像はありません</span>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <br /><br />
                <strong>緊急連絡先</strong>
                <hr />
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">氏名(漢字)</label>
                        <div class="bisected-input-wrap">
                          <div class="bisected-input">
                            <input type="text" class="form-control" [(ngModel)]="form.emLastName" name="emLastName" required>
                          </div>
                          <span>&nbsp;</span>
                          <div class="bisected-input">
                            <input type="text" class="form-control" [(ngModel)]="form.emFirstName" name="emFirstName" required>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">氏名(ふりがな)</label>
                        <div class="bisected-input-wrap">
                          <div class="bisected-input">
                            <input type="text" class="form-control" [(ngModel)]="form.emLastNameKana" name="emLastNameKana" required>
                          </div>
                          <span>&nbsp;</span>
                          <div class="bisected-input">
                            <input type="text" class="form-control" [(ngModel)]="form.emFirstNameKana" name="emFirstNameKana" required>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">続柄</label>
                        <select class="col-md-6 form-control" [(ngModel)]="form.emRelationship" name="emRelationship" required>
                          <option value="0">妻</option>
                          <option value="1">夫</option>
                          <option value="2">父</option>
                          <option value="3">母</option>
                          <option value="4">子</option>
                          <option value="5">兄弟、姉妹</option>
                          <option value="6">その他</option>
                        </select>
                      </div>
                    </div>
                    <div class="form-group row">
                      <div class="col-md-6">
                        <label class="col-form-label">電話番号</label>
                        <div class="trisection-input-wrap">
                          <div class="trisection-input">
                            <input type="text" class="form-control" [(ngModel)]="form.emPhone1" name="emPhone1" required pattern="^[\x01-\x7E]+$">
                          </div>
                          <span>&nbsp;</span>
                          <div class="trisection-input">
                            <input type="text" class="form-control" [(ngModel)]="form.emPhone2" name="emPhone2" required pattern="^[\x01-\x7E]+$">
                          </div>
                          <span>&nbsp;</span>
                          <div class="trisection-input">
                            <input type="text" class="form-control" [(ngModel)]="form.emPhone3" name="emPhone3" required pattern="^[\x01-\x7E]+$">
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <br /><br />
                <strong>店舗管理情報</strong>
                <hr />
                <div class="row">
                  <div class="col-md-12">
                    <div class="row align-items-end">
                      <div class="col-md-6 form-group">
                        <label class="col-form-label">入退館キー
                          @if (form.status === 6) {
                            <span class="text-danger"> ※会員情報画面の状態/備考アイコンをクリックして仮登録処理を行ってください。</span>
                          }
                          @if (form.status === 4 && form.payment?.status !== 0) {
                            <span class="text-danger"> ※仮登録会員が入退館キーを選択する場合は、支払方法の登録が必須です。</span>
                          }
                        </label>
                        <select class="form-control" [(ngModel)]="selectedKeyNumber" name="keyNumber"
                          [disabled]="form.status === 6 || (form.status === 4 && form.payment?.status !== 0)">
                          <option [ngValue]="null">選択してください</option>
                          @for (item of keyItems; track item.id) {
                            <option [ngValue]="item">{{ item.keyNumber }}{{ item.studioPlan?.studioPlanType?.name ? ' (' + item.studioPlan.studioPlanType.name + ')' : '' }}</option>
                          }
                        </select>
                        @if (form.status === 4 && originalStudioPlanId !== null && originalStudioPlanId !== form.studioPlanId && !selectedKeyNumber) {
                          <span class="text-danger"> ※仮登録会員の区分コースを変更する場合は、入退館キーの登録が必須です。</span>
                        }
                      </div>
                      @if ((form.status === 1 || form.status === 2) && form.keyNumberId) {
                        <div class="col-md-6 form-group">
                          <button type="button" class="btn btn-danger text-white" (click)="showDeleteKeyModal = true">
                            <i class="fa s-icon-delete"></i>&nbsp;入退館キー削除
                          </button>
                        </div>
                      }
                    </div>
                    <div class="form-group align-items-end">
                      <div>
                        <label class="col-form-label">ロッカー:
                          @if (form.isUseLocker === 0) {
                            <span>ロッカーを使用する({{ form.lockerNumber?.lockerNumber }})</span>
                          }
                          @if (form.isUseLocker === 1) {
                            <span>ロッカーを使用しない</span>
                          }
                        </label>
                        @if (form.status === 0) {
                          <div>
                            <a class="btn btn-success" [routerLink]="['/notification', form.id, 'locker']" title="ロッカー諸届申請">
                              <i class="fa fa-inbox fa-fw"></i><span>&nbsp;ロッカー諸届申請</span>
                            </a>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              }
              <br /><br />
              <!-- 旧版 member-edit.html:376-383 のお申し込みページ初回請求キャプチャ -->
              @if (form?.firstInvoice) {
                <div>
                  <strong>お申し込みページ初回請求キャプチャ</strong>
                  <hr />
                  <div class="row">
                    <blockquote class="ml-3 pt-2 col-md-12" style="border-left: solid 0.5em #ddd;"
                                [innerHTML]="form.firstInvoice.html"></blockquote>
                  </div>
                  <br />
                </div>
              }
              <strong>入会アンケート結果</strong>
              <hr />
              <div class="row">
                <div class="col-md-12">
                  @for (qr of questionaryResults; track qr.id) {
                    <div class="form-group">
                      <ul class="questionary-result-list">
                        <li class="questionary-result-q">{{ qr.questionaryMaster.questionaryContent }}</li>
                        @if (qr.questionaryMaster.freeFlg === 0) {
                          <li class="questionary-result-a">{{ qr.questionaryMaster.selectColumn }}</li>
                        }
                        @if (qr.questionaryMaster.freeFlg === 1) {
                          <li class="questionary-result-a">{{ qr.inputMemoResult }}</li>
                        }
                        @if (qr.questionaryMaster.freeFlg === 0 && qr.inputMemoResult != null) {
                          <li class="questionary-result-fq">{{ qr.questionaryMaster.inputMemoContent }}</li>
                          <li class="questionary-result-fa">{{ qr.inputMemoResult }}</li>
                        }
                      </ul>
                    </div>
                  }
                </div>
              </div>
              <br /><br />
              <div class="row">
                <div class="col-md-12 text-right">
                  <a routerLink="/member" class="btn bg-close text-white">
                    <i class="fa s-icon-cancel"></i>&nbsp;閉じる
                  </a>
                  @if (form.status === 4 && selectedKeyNumber) {
                    <button type="button" class="btn btn-success ml-2" (click)="openSaveModal()" [disabled]="!canSave()">
                      @if (saving) { <span class="fa fa-spinner fa-spin fa-fw"></span> }
                      <i class="fa s-icon-save"></i>&nbsp;保存
                    </button>
                  } @else {
                    <button type="button" class="btn btn-success ml-2" (click)="save()" [disabled]="!canSave()">
                      @if (saving) { <span class="fa fa-spinner fa-spin fa-fw"></span> }
                      <i class="fa s-icon-save"></i>&nbsp;保存
                    </button>
                  }
                </div>
              </div>
            </form>
          }
        </div>
      </div>
    </div>
  </div>
</div>

<!-- 画像アップロード 1枚目 -->
@if (showUploadModal1) {
  <div class="modal-backdrop fade show" (click)="showUploadModal1 = false"></div>
  <div class="modal fade show d-block" id="imageRemoveModalOne" tabindex="-1" role="dialog" aria-labelledby="imageRemoveModalOneLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">本人画像(1枚目)アップロード</h5>
          <button type="button" class="close" (click)="showUploadModal1 = false"><span>&times;</span></button>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <label class="input-group-btn">
              <span class="btn btn-light">
                <i class="fa s-icon-image"></i>&nbsp;ファイル選択
                <input type="file" class="d-none" (change)="onFileSelect($event, 1)">
              </span>
            </label>
            <input type="text" class="form-control bg-white border-0" readonly [value]="selectedFile1?.name ?? '選択されていません'">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showUploadModal1 = false">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" (click)="uploadImage(1)" [disabled]="!selectedFile1">
            <i class="fa fa-arrow-up"></i>&nbsp;アップロード
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- 画像アップロード 2枚目 -->
@if (showUploadModal2) {
  <div class="modal-backdrop fade show" (click)="showUploadModal2 = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">本人画像(2枚目)アップロード</h5>
          <button type="button" class="close" (click)="showUploadModal2 = false"><span>&times;</span></button>
        </div>
        <div class="modal-body">
          <div class="input-group">
            <label class="input-group-btn">
              <span class="btn btn-light">
                <i class="fa s-icon-image"></i>&nbsp;ファイル選択
                <input type="file" class="d-none" (change)="onFileSelect($event, 2)">
              </span>
            </label>
            <input type="text" class="form-control bg-white border-0" readonly [value]="selectedFile2?.name ?? '選択されていません'">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showUploadModal2 = false">
            <i class="fa s-icon-cancel"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" (click)="uploadImage(2)" [disabled]="!selectedFile2">
            <i class="fa fa-arrow-up"></i>&nbsp;アップロード
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- 画像削除 1枚目 -->
@if (showRemoveModal1) {
  <div class="modal-backdrop fade show" (click)="showRemoveModal1 = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="imageRemoveModalOneLabel">本人画像(1枚目)削除</h5>
          <button type="button" class="close" (click)="showRemoveModal1 = false"><span>&times;</span></button>
        </div>
        <form novalidate name="tempPasswordForm">
        <div class="modal-body">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">本人画像(1枚目)を削除します。よろしいですか。</label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showRemoveModal1 = false">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" (click)="removeImage(1)">
            <i class="fa s-icon-delete"></i>&nbsp;削除
          </button>
        </div>
        </form>
      </div>
    </div>
  </div>
}

<!-- 画像削除 2枚目 -->
@if (showRemoveModal2) {
  <div class="modal-backdrop fade show" (click)="showRemoveModal2 = false"></div>
  <div class="modal fade show d-block" id="imageRemoveModalTwo" tabindex="-1" role="dialog" aria-labelledby="imageRemoveModalTwoLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="imageRemoveModalTwoLabel">本人画像(2枚目)削除</h5>
          <button type="button" class="close" (click)="showRemoveModal2 = false"><span>&times;</span></button>
        </div>
        <form novalidate name="tempPasswordForm">
        <div class="modal-body">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">本人画像(2枚目)を削除します。よろしいですか。</label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn bg-close text-white" type="button" (click)="showRemoveModal2 = false">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" (click)="removeImage(2)">
            <i class="fa s-icon-delete"></i>&nbsp;削除
          </button>
        </div>
        </form>
      </div>
    </div>
  </div>
}

<!-- パスワードリセット -->
@if (showResetPasswordModal) {
  <div class="modal-backdrop fade show" (click)="showResetPasswordModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger">
          <h5 class="modal-title">パスワードリセット</h5>
          <button type="button" class="close" (click)="showResetPasswordModal = false">
            <span class="text-white">&times;</span>
          </button>
        </div>
        <div class="modal-body" style="background-color: #FFF6F8 !important">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">
              パスワードを登録生年月日に設定します。<br>
              設定したパスワードは会員情報保存後に有効となります。<br>
              マイページログイン後、任意のパスワードへ変更してください。
            </label>
          </div>
        </div>
        <div class="modal-footer" style="background-color: #FFF6F8 !important">
          <button class="btn bg-close text-white" type="button" (click)="showResetPasswordModal = false">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-success" type="button" (click)="resetPassword()">
            <i class="fa s-icon-reset"></i>&nbsp;リセット
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- メールアドレス削除 -->
@if (showDeleteEmailModal) {
  <div class="modal-backdrop fade show" (click)="showDeleteEmailModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger">
          <h5 class="modal-title">メールアドレスの削除</h5>
          <button type="button" class="close" (click)="showDeleteEmailModal = false">
            <span class="text-white">&times;</span>
          </button>
        </div>
        <div class="modal-body" style="background-color: #FFF6F8 !important">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">メールアドレスを削除します。よろしいですか。</label>
          </div>
        </div>
        <div class="modal-footer" style="background-color: #FFF6F8 !important">
          <button class="btn bg-close text-white" type="button" (click)="showDeleteEmailModal = false">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-danger" type="button" (click)="deleteEmail()">
            <i class="fa s-icon-delete"></i>&nbsp;削除
          </button>
        </div>
      </div>
    </div>
  </div>
}

<!-- 入退館キー削除 -->
@if (showDeleteKeyModal) {
  <div class="modal-backdrop fade show" (click)="showDeleteKeyModal = false"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog" role="document">
      <div class="modal-content border-danger">
        <div class="modal-header bg-danger">
          <h5 class="modal-title">入退館キーの削除</h5>
          <button type="button" class="close" (click)="showDeleteKeyModal = false">
            <span class="text-white">&times;</span>
          </button>
        </div>
        <div class="modal-body" style="background-color: #FFF6F8 !important">
          <div class="form-group row">
            <label class="col-md-12 col-form-label">入退館キーを削除します。よろしいですか。</label>
          </div>
        </div>
        <div class="modal-footer" style="background-color: #FFF6F8 !important">
          <button class="btn bg-close text-white" type="button" (click)="showDeleteKeyModal = false">
            <i class="fa s-icon-cancel fa-fw"></i>&nbsp;閉じる
          </button>
          <button class="btn btn-danger" type="button" (click)="deleteStudioKey()">
            <i class="fa s-icon-delete"></i>&nbsp;削除
          </button>
        </div>
      </div>
    </div>
  </div>
}

@if (showSaveModal) {
  <div class="modal-backdrop fade show" (click)="showSaveModal = false"></div>
  <div class="modal fade show d-block" id="saveModal" tabindex="-1" role="dialog" aria-labelledby="saveModalLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="saveModalLabel">保存</h5>
          <button type="button" class="close" (click)="showSaveModal = false">
            <span>&times;</span>
          </button>
        </div>
        <form novalidate name="saveModalForm">
          <div class="modal-body">
            <label>会員データを保存して、本登録処理を行います。</label>
          </div>
          <div class="modal-body d-none" aria-hidden="true">
            <label>会員データを保存します。あわせて本登録処理を行いますか？</label>
            <div class="custom-control custom-radio">
              <input type="radio" id="is_taking_three_meals_1" class="custom-control-input"
                [(ngModel)]="isKeptTemporaryStatus" value="0" name="isKeptTemporaryStatus">
              <label class="custom-control-label" for="is_taking_three_meals_1">本登録処理を行う</label>
            </div>
            <div class="custom-control custom-radio">
              <input type="radio" id="is_taking_three_meals_0" class="custom-control-input"
                [(ngModel)]="isKeptTemporaryStatus" value="1" name="isKeptTemporaryStatus">
              <label class="custom-control-label" for="is_taking_three_meals_0">仮登録のままにする</label>
            </div>
          </div>
          @if (insuranceProduct) {
            <div class="modal-body">
              <div class="custom-control custom-checkbox">
                <input class="custom-control-input" [(ngModel)]="isInsured" id="is_insured" type="checkbox" name="isInsured">
                <label class="custom-control-label" for="is_insured">保険適応する({{ insuranceProduct.taxIncAmount }}円/月)</label>
              </div>
            </div>
          }
          <div class="modal-footer">
            <button class="btn bg-close text-white" type="button" (click)="showSaveModal = false">
              <i class="fa s-icon-cancel"></i>&nbsp;閉じる
            </button>
            <button class="btn btn-success" type="button" (click)="save()" [disabled]="!canSave()">
              <i class="fa s-icon-save"></i>&nbsp;保存
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
}
  `
})
export class MemberEditComponent implements OnInit {
  private readonly businessClock = inject(BusinessClock);
  private readonly clockContext = clockView(true);

  private pageTitleService = inject(PageTitleService);
  private route = inject(ActivatedRoute);
  private memberService = inject(MemberService);
  private studioService = inject(StudioService);
  private questionaryService = inject(QuestionaryService);
  private keyNumberService = inject(KeyNumberService);
  private notificationService = inject(NotificationService);
  private transactionService = inject(TransactionService);
  private flash = inject(FlashService);

  loading = true;
  saving = false;
  form: any = null;
  private originalUniqueValues: Record<UniqueField, string> = { loginId: '', email: '' };
  readonly uniqueChecks: Record<UniqueField, UniqueCheck> = {
    loginId: { value: '', state: 'idle', request: 0 },
    email: { value: '', state: 'idle', request: 0 },
  };
  payment: any = {};
  studioPlans: any[] = [];
  keyItems: any[] = [];
  selectedKeyNumber: any = null;
  originalStudioPlanId: number | null = null;
  insuranceProduct: any = null;
  isInsured = false;
  isKeptTemporaryStatus = '0';
  dayOfCreateTransaction = 11;
  questionaryResults: any[] = [];
  selectedFile1: File | null = null;
  selectedFile2: File | null = null;
  showUploadModal1 = false;
  showUploadModal2 = false;
  showRemoveModal1 = false;
  showRemoveModal2 = false;
  showResetPasswordModal = false;
  showDeleteEmailModal = false;
  showDeleteKeyModal = false;
  showSaveModal = false;
  isRotatable = true;

  readonly statusOptions = STATUS_OPTIONS;
  readonly paymentMethodOptions = PAYMENT_METHOD_OPTIONS;

  readonly birthYears: number[] = (() => {
    const now = new JstDate().getFullYear();
    const years: number[] = [];
    for (let y = now - 15; y >= now - 115; y--) years.push(y);
    return years;
  })();
  readonly birthMonths = [1,2,3,4,5,6,7,8,9,10,11,12];
  readonly birthDates = Array.from({length: 31}, (_, i) => i + 1);

  get imageSrc1(): string {
    return `${environment.apiBaseUrl}/member/${this.form?.id}/picture/${this.form?.uploadedImg1}`;
  }
  get imageSrc2(): string {
    return `${environment.apiBaseUrl}/member/${this.form?.id}/picture/${this.form?.uploadedImg2}`;
  }

  async ngOnInit(): Promise<void> {
    this.pageTitleService.set('会員管理', 's-icon-members');
    this.loading = true;
    this.insuranceProduct = null;
    const memberId = Number(this.route.snapshot.params['memberId']);
    const [member, questionaryRaw, schedule] = await Promise.all([
      this.memberService.getOne(memberId),
      this.questionaryService.getQuestionaryResultByMemberId(memberId),
      this.transactionService.getBatchSchedule().catch(() => 11),
    ]);
    const scheduleDay = Number((schedule as any)?.createTransaction ?? schedule);
    this.dayOfCreateTransaction = Number.isFinite(scheduleDay) ? scheduleDay : 11;
    if (member) {
      this.form = { ...member };
      for (const field of ['loginId', 'email'] as const) {
        this.originalUniqueValues[field] = this.uniqueValue(field);
        this.onUniqueValueChange(field);
      }
      this.form.studioPlanId = this.form.studioPlan?.id ?? this.form.studioPlanId;
      this.originalStudioPlanId = this.form.studioPlanId != null ? Number(this.form.studioPlanId) : null;
      this.payment = member.payment ?? {};
      const zipParts = (this.form.zip ?? '').split('-');
      this.form.zip1 = zipParts[0] ?? '';
      this.form.zip2 = zipParts[1] ?? '';
      const phoneParts = (this.form.phone ?? '').split('-');
      this.form.phone1 = phoneParts[0] ?? '';
      this.form.phone2 = phoneParts[1] ?? '';
      this.form.phone3 = phoneParts[2] ?? '';
      const emPhoneParts = (this.form.emPhone ?? '').split('-');
      this.form.emPhone1 = emPhoneParts[0] ?? '';
      this.form.emPhone2 = emPhoneParts[1] ?? '';
      this.form.emPhone3 = emPhoneParts[2] ?? '';
      const birthParts = (this.form.birthday ?? '').split('-');
      if (birthParts.length >= 3) {
        this.form.birth1 = parseInt(birthParts[0]);
        this.form.birth2 = parseInt(birthParts[1]);
        this.form.birth3 = parseInt(birthParts[2]);
      }
      const studioId = member.studio?.id ?? member.studioId;
      if (studioId) {
        const [studioData, keyData, insuranceProduct] = await Promise.all([
          this.studioService.list(studioId) as Promise<any>,
          this.studioService.getKeyNumber(studioId) as Promise<any[]>,
          this.studioService.getInsuranceProduct(studioId),
        ]);
        this.studioPlans = studioData?.studioPlans ?? studioData?.plans ?? [];
        this.insuranceProduct = insuranceProduct;
        const memberId2 = this.form.id;
        this.keyItems = (keyData ?? []).filter((v: any) =>
          (v.member === null || (v.member && (v.member.id === memberId2 || v.id !== v.member.keyNumberId))) && !v.isStaff
        );
        const currentKeyId = this.form.keyNumber?.id ?? this.form.keyNumberId;
        const currentKey = this.keyItems.find((v: any) => v.id === currentKeyId) ??
          this.keyItems.find((v: any) => v.member?.id === memberId2 && v.id === v.member?.keyNumberId) ??
          null;
        if (currentKey) {
          this.form.keyNumber = currentKey;
          this.form.keyNumberId = currentKey.id;
        }
        this.selectedKeyNumber = currentKey;
      }
    }
    this.questionaryResults = groupQuestionaryResults(questionaryRaw as any[] ?? []);
    this.loading = false;
  }

  async searchZipCode(): Promise<void> {
    const zip = `${this.form?.zip1}-${this.form?.zip2}`;
    if (!zip || zip === '-') return;
    const data: any = await this.memberService.getAddress(zip);
    if (data) {
      this.form.province = data.address1;
      this.form.city = data.address2;
      this.form.area = data.address3;
    }
  }

  async changeIsDeliverEmail(): Promise<void> {
    const result = await this.memberService.upsert({ id: this.form.id, isDeliverEmail: this.form.isDeliverEmail });
    // 旧版 member.js:865-870: 結果が空なら Flash・先頭へscroll・state reload
    if (!result) {
      this.flash.create('success', 'メール配信状態を更新しました');
      window.scrollTo(0, 0);
      await this.ngOnInit();
    }
  }

  async updateNote(): Promise<void> {
    await this.memberService.upsert({ id: this.form.id, note: this.form.note });
    // 旧版 member.js:751-754 の成功側: Flash・先頭へscroll・state reload（失敗時は ApiService が /error へ遷移）
    this.flash.create('success', '会員情報を更新しました');
    window.scrollTo(0, 0);
    await this.ngOnInit();
  }

  resetPassword(): void {
    this.showResetPasswordModal = false;
    // The old PJ only sets the password field to the birthday (YYYYMMDD); it takes effect
    // when the member is saved, as the modal text tells the admin. /member/temp/pass is
    // reserved for the member-list register link and must not be called here.
    const pad2 = (n: any) => String(n).padStart(2, '0');
    this.form.password = `${this.form.birth1}${pad2(this.form.birth2)}${pad2(this.form.birth3)}`;
  }

  onFileSelect(event: Event, imgNum: 1 | 2): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (imgNum === 1) this.selectedFile1 = file;
    else this.selectedFile2 = file;
  }

  async uploadImage(imgNum: 1 | 2): Promise<void> {
    const file = imgNum === 1 ? this.selectedFile1 : this.selectedFile2;
    if (!file || !this.form?.id) return;
    const compressedFile = await compressImageForUpload(file);
    const fd = new FormData();
    fd.append('file', compressedFile);
    await this.memberService.uploadPicture(this.form.id, imgNum, fd);
    if (imgNum === 1) { this.showUploadModal1 = false; this.selectedFile1 = null; }
    else { this.showUploadModal2 = false; this.selectedFile2 = null; }
    // The upload response is storage metadata, so reload the member to get the saved filename.
    await this.ngOnInit();
  }

  async removeImage(imgNum: 1 | 2): Promise<void> {
    // The old PJ posts the member wrapped as { member: { ..., imgNumber } } (same contract
    // as picture/rotate), not { memberId, pictureId }.
    await this.memberService.removePicture({ member: { ...this.form, imgNumber: imgNum } });
    if (imgNum === 1) { this.form.uploadedImg1 = null; this.showRemoveModal1 = false; }
    else { this.form.uploadedImg2 = null; this.showRemoveModal2 = false; }
  }

  async rotateImage(imgNum: 1 | 2, deg: number): Promise<void> {
    if (!this.isRotatable || !this.form?.id) return;
    this.isRotatable = false;
    try {
      const result = await this.memberService.updatePictureRotate({ member: { ...this.form, imgNumber: imgNum, deg } });
      if (result) {
        if (imgNum === 1) this.form.uploadedImg1 = result;
        else this.form.uploadedImg2 = result;
      }
    } finally {
      window.setTimeout(() => { this.isRotatable = true; }, 2000);
    }
  }

  private uniqueValue(field: UniqueField): string {
    return String(this.form?.[field] ?? '');
  }

  onUniqueValueChange(field: UniqueField): void {
    const check = this.uniqueChecks[field];
    check.request++;
    check.value = this.uniqueValue(field);
    check.state = 'idle';
  }

  async checkDuplicate(field: UniqueField): Promise<void> {
    const value = this.uniqueValue(field);
    const check = this.uniqueChecks[field];
    const request = ++check.request;
    check.value = value;
    check.state = 'idle';
    // The exists API includes the current member, so unchanged values must be skipped.
    if (!value || value === this.originalUniqueValues[field]) return;
    if (field === 'loginId' && !/^[\x01-\x7E]+$/.test(value)) return;
    check.state = 'pending';
    try {
      const exists = await this.memberService.exists(field, value);
      // A slow response must not overwrite the validation of a newer input.
      if (request !== check.request || value !== this.uniqueValue(field)) return;
      check.state = exists === true ? 'duplicate' : exists === false ? 'valid' : 'error';
    } catch {
      if (request === check.request && value === this.uniqueValue(field)) check.state = 'error';
    }
  }

  canSave(): boolean {
    if (!this.form || this.saving) return false;
    const status = Number(this.form.status);
    if (![0, 4, 5, 6].includes(status)) return false;
    // 旧版 member-edit.html:177 type="email" の形式不正は memberEditForm.$invalid として保存を無効にする
    if (this.form.email && Validators.email({ value: this.form.email } as AbstractControl)) return false;
    for (const field of ['loginId', 'email'] as const) {
      if (field === 'loginId' && status === 5) continue;
      const value = this.uniqueValue(field);
      if (value && value !== this.originalUniqueValues[field]) {
        const check = this.uniqueChecks[field];
        if (check.value !== value || check.state !== 'valid') return false;
      }
    }
    if (status !== 5) {
      const requiredValues = [
        this.form.studioPlanId, this.form.isWebRegister,
        this.form.lastName, this.form.firstName, this.form.lastNameKana, this.form.firstNameKana,
        this.form.sex, this.form.zip1, this.form.zip2, this.form.province, this.form.city, this.form.area,
        this.form.birth1, this.form.birth2, this.form.birth3, this.form.isJapanese, this.form.loginId,
        this.form.phone1, this.form.phone2, this.form.phone3,
        this.form.emLastName, this.form.emFirstName, this.form.emLastNameKana, this.form.emFirstNameKana,
        this.form.emRelationship, this.form.emPhone1, this.form.emPhone2, this.form.emPhone3,
      ];
      if (requiredValues.some(value => value === null || value === undefined || String(value).trim() === '')) return false;
      const asciiValues = [
        this.form.zip1, this.form.zip2, this.form.loginId,
        this.form.phone1, this.form.phone2, this.form.phone3,
        this.form.emPhone1, this.form.emPhone2, this.form.emPhone3,
      ];
      if (asciiValues.some(value => !/^[\x01-\x7E]+$/.test(String(value)))) return false;
    }
    const studioPlanId = this.form.studioPlanId != null ? Number(this.form.studioPlanId) : null;
    if (status === 4 && this.originalStudioPlanId !== null && this.originalStudioPlanId !== studioPlanId && !this.selectedKeyNumber) {
      return false;
    }
    return true;
  }

  openSaveModal(): void {
    if (!this.canSave()) return;
    this.isKeptTemporaryStatus = '0';
    this.isInsured = false;
    this.showSaveModal = true;
  }

  async deleteEmail(): Promise<void> {
    this.showDeleteEmailModal = false;
    const result = await this.memberService.upsert({ id: this.form.id, email: null });
    this.form.email = null;
    // 旧版 member.js:838-843: 結果が空なら Flash・先頭へscroll（reloadなし）
    if (!result) {
      this.flash.create('success', '会員情報を更新しました');
      window.scrollTo(0, 0);
    }
  }

  async deleteStudioKey(): Promise<void> {
    this.showDeleteKeyModal = false;
    if (this.form.keyNumber?.id) {
      // The old PJ appends the released key number to the member note inside the release
      // payload and lets the release API detach the key (no extra member upsert).
      const note = `${this.form.note || ''} 入退館キー: ${this.form.keyNumber.keyNumber}`;
      await this.keyNumberService.release({ id: this.form.keyNumber.id, memberId: this.form.id, note });
      this.form.note = note;
    }
    this.form.keyNumberId = null;
    this.form.keyNumber = null;
    this.selectedKeyNumber = null;
  }

  async save(): Promise<void> {
    if (!await this.businessClock.confirm(this.clockContext)) return;
    if (!this.canSave()) return;
    this.showSaveModal = false;
    this.saving = true;
    try {
      const status = Number(this.form.status);
      const studioId = Number(this.form.studio?.id ?? this.form.studioId);
      const studioPlanId = this.form.studioPlanId != null ? Number(this.form.studioPlanId) : null;
      const changePlan = status !== 5 && status !== 6 && this.originalStudioPlanId !== studioPlanId;
      const payload: any = { ...this.form };
      payload.studioPlanId = studioPlanId;
      payload.zip = `${payload.zip1}-${payload.zip2}`;
      payload.phone = `${payload.phone1}-${payload.phone2}-${payload.phone3}`;
      if (status !== 5) payload.emPhone = `${payload.emPhone1}-${payload.emPhone2}-${payload.emPhone3}`;
      const pad2 = (n: number) => String(n).padStart(2, '0');
      payload.birthday = `${payload.birth1}-${pad2(payload.birth2)}-${pad2(payload.birth3)}`;
      const presentationKeys = [
        'memberCode', 'uploadedImg1', 'uploadedImg2', 'studioPlan', 'zip1', 'zip2',
        'phone1', 'phone2', 'phone3', 'emPhone1', 'emPhone2', 'emPhone3',
        'birth1', 'birth2', 'birth3', 'status', 'studioLockerPlan', 'keyNumberId',
        'keyNumber', 'lockerNumberId', 'lockerNumber', 'isUseLocker', 'paymentId',
        'createdAt', 'updatedAt', 'deletedAt', 'disabledAt', 'expelledAt',
        'expulsionReason', 'isEnableWebLogin', 'isExpulsionAlert', 'isSendKey',
        'lockerNumberProduct', 'payment', 'studio',
      ];
      for (const key of presentationKeys) delete payload[key];

      if (status === 5) {
        await this.finishMemberUpdate(payload, changePlan, studioId, studioPlanId);
        return;
      }

      const currentKey = this.form.keyNumber;
      const currentKeyId = this.form.keyNumberId;
      const selectedKey = this.selectedKeyNumber ? { ...this.selectedKeyNumber } : null;

      if (!selectedKey && currentKeyId) {
        if (currentKey?.id) {
          await this.keyNumberService.release({ id: currentKey.id, memberId: this.form.id });
        }
        await this.finishMemberUpdate({ id: this.form.id, keyNumberId: null }, changePlan, studioId, studioPlanId);
      } else if (selectedKey && (!currentKeyId || selectedKey.id !== currentKey?.id)) {
        const keyResult = await this.keyNumberService.update({ ...selectedKey, memberId: this.form.id });
        if (!keyResult || keyResult === 'No target') {
          await this.finishMemberUpdate(payload, changePlan, studioId, studioPlanId);
          window.alert('入退館キーの更新に失敗しました。別の入退館キーを設定してください');
        } else if (!currentKeyId && (status === 4 || status === 6) && this.isKeptTemporaryStatus === '0') {
          if (this.isInsured) await this.createInsurance(studioId, new JstDate());
          await this.delay(500);
          await this.enableMember(studioId, studioPlanId);
          await this.finishMemberUpdate(payload, changePlan, studioId, studioPlanId);
        } else {
          await this.finishMemberUpdate(payload, changePlan, studioId, studioPlanId);
        }
      } else if (selectedKey && status === 4 && this.isKeptTemporaryStatus === '0') {
        await this.notificationService.removeInsuranceForUpdateMember(this.form.id);
        if (this.isInsured) await this.createInsurance(studioId, new JstDate());
        await this.delay(500);
        await this.enableMember(studioId, studioPlanId);
        await this.finishMemberUpdate(payload, changePlan, studioId, studioPlanId);
      } else if (selectedKey && status === 4 && this.isKeptTemporaryStatus === '1') {
        if (this.isInsured) {
          await this.createInsurance(studioId, new JstDate(this.form.desiredStartDate));
        } else {
          await this.notificationService.removeInsuranceForUpdateMember(this.form.id);
        }
        await this.finishMemberUpdate(payload, changePlan, studioId, studioPlanId);
      } else {
        await this.finishMemberUpdate(payload, changePlan, studioId, studioPlanId);
      }
    } finally {
      this.saving = false;
    }
  }

  private async createInsurance(studioId: number, desiredStartDate: Date): Promise<void> {
    await this.notificationService.createInsuranceForUpdateMember({
      studioId,
      memberId: this.form.id,
      desiredStartDate,
    });
  }

  private async enableMember(studioId: number, studioPlanId: number | null): Promise<void> {
    const now = new JstDate();
    await this.memberService.enable({
      memberId: this.form.id,
      studioId,
      studioPlanId,
      paymentMethod: this.form.payment?.paymentMethod ?? this.payment?.paymentMethod,
      startDate: new JstDate(now.getFullYear(), now.getMonth(), now.getDate()),
    });
  }

  private async finishMemberUpdate(
    payload: any,
    changePlan: boolean,
    studioId: number,
    studioPlanId: number | null,
  ): Promise<void> {
    const result = await this.memberService.upsert(payload);
    if (result === 'Validation error') {
      window.alert('すでに使用されているログインIDです。別のログインIDを設定してください。');
    } else if (changePlan) {
      const now = new JstDate();
      const modify = now.getDate() >= this.dayOfCreateTransaction ? 2 : 1;
      await this.memberService.createHistory({
        memberId: this.form.id,
        studioId,
        studioPlanId,
        startDate: new JstDate(now.getFullYear(), now.getMonth() + modify, 1),
        endDate: new JstDate(now.getFullYear(), now.getMonth() + modify, 0),
      });
    }
    // 旧版 member.js:732-736: ログインID重複以外は成功 Flash（その後 先頭へscroll・state reload）
    if (result !== 'Validation error') this.flash.create('success', '会員情報を更新しました');
    await this.ngOnInit();
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }
}
